import { useState, useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { MODALIDADES_PSS } from "../utils/HospitalizacionConstant";
import HojasQuirurgicasService from "../../../services/hospitalizacion/HojasQuirurgicasService";
import {
  construirValidacionData,
  procesarEnvioCoberturas,
  procesarEnvioAnexos,
  construirMensajeResultado,
} from "../utils/CrearValidacionHojaQXUtils";
import { useMountEffect } from "../../../hooks/LifeCycle";
import useEmpresa from "../../../hooks/useEmpresa";
import { LOCAL_STORAGE, ENCUESTA_CES_HORARIO } from "../../../utils/Constant";
import { esFechaDelDiaActual, estaHoraEntreRango } from "../../../utils/DateUtils";

const useValidacionesHojaQX = ({
  authUserData,
  consultarPaciente,
  consultarPacienteData,
  consultarPacientesData,
  consultarTipoPssNoMedicoData,
  obtenerEspecialidadesMedicas,
  numeroIngreso: numeroIngresoParam,
}) => {
  const { numeroIngreso } = useParams();
  const empresa = useEmpresa();
  const finalNumeroIngreso = numeroIngresoParam || numeroIngreso;

  const [pacienteIngresado, setPacienteIngresado] = useState(null);
  const [coberturasMedicos, setCoberturasMedicos] = useState([]);
  const [documentosAnexos, setDocumentosAnexos] = useState([]);
  const [coberturasCompletas, setCoberturasCompletas] = useState([]);
  const [anexosCompletos, setAnexosCompletos] = useState([]);
  const [coberturasEnviadas, setCoberturasEnviadas] = useState(new Set());
  const [archivosEnviados, setArchivosEnviados] = useState(new Set());
  const [hojaQuirurgicaLoading, setHojaQuirurgicaLoading] = useState(false);
  const [enviandoCoberturas, setEnviandoCoberturas] = useState(false);
  const [currentFormData, setCurrentFormData] = useState(null);
  const [coberturasDuplicadas, setCoberturasDuplicadas] = useState([]);
  const [coberturasDuplicadasIds, setCoberturasDuplicadasIds] = useState(
    new Set()
  );
  const [resultadoEnvioPendiente, setResultadoEnvioPendiente] = useState(null);

  const cargarPacienteConHojaQX = useCallback(() => {
    if (!finalNumeroIngreso) return;

    const pacienteData =
      consultarPacienteData?.data?.ingresos?.find(
        (p) => p.numeroIngreso === finalNumeroIngreso
      ) ||
      consultarPacientesData?.data?.ingresos?.find(
        (p) => p.numeroIngreso === finalNumeroIngreso
      );

    if (pacienteData) {
      const pacienteCompleto = {
        ...pacienteData,
        secId: `26${pacienteData.numeroIngreso}`,
        estadoHojaQX: "Pendiente",
        fechaUltimoCambio: new Date().toISOString(),
      };

      setPacienteIngresado(pacienteCompleto);
      setCoberturasMedicos([]);
      setDocumentosAnexos([]);
      setCoberturasCompletas([]);
      setAnexosCompletos([]);
      setCoberturasDuplicadasIds(new Set());
      setCurrentFormData(null);
      setDocumentosAnexos([]);
    } else {
      setPacienteIngresado(null);
    }
  }, [
    finalNumeroIngreso,
    consultarPacienteData,
    consultarPacientesData,
  ]);

  const handleSubmitHojaQuirurgica = useCallback(
    async (formData) => {
      setHojaQuirurgicaLoading(true);
      const formDataConIds = {
        ...formData,
        attachedFiles: formData.attachedFiles || [],
      };
      setCurrentFormData(formDataConIds);

      try {
        if (
          formData.coberturasMedicos &&
          formData.coberturasMedicos.length > 0
        ) {
          setCoberturasMedicos((prev) => {
            const existingKeys = new Set(prev.map((c) => c.key));
            const timestamp = Date.now();

            const nuevasCoberturas = formData.coberturasMedicos
              .map((item, index) => {
                const baseKey = item.id || `new_${timestamp}_${index}`;
                let uniqueKey = baseKey;
                let counter = 0;

                while (existingKeys.has(uniqueKey)) {
                  uniqueKey = `${baseKey}_${counter}`;
                  counter++;
                }

                return {
                  key: uniqueKey,
                  id: item.id,
                  codigoCobertura: item?.cobertura.cobertura,
                  nombreCobertura: item.cobertura?.nombreCobertura || "",
                  codigoPrestador: item.medico?.codigo || "",
                  nombrePrestador: item.medico?.nombre || "",
                  status: "Pendiente",
                  fechaCreacion: new Date().toLocaleDateString(),
                };
              })
              .filter((c) => !existingKeys.has(c.key));

            return [...prev, ...nuevasCoberturas];
          });

          setCoberturasCompletas((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const nuevasCoberturasCompletas = formData.coberturasMedicos.filter(
              (c) => !existingIds.has(c.id)
            );
            return [...prev, ...nuevasCoberturasCompletas];
          });
        }

        if (formData.attachedFiles && formData.attachedFiles.length > 0) {
          const primeraCobertura = formData.coberturasMedicos?.[0];
          const nuevosAnexos = formData.attachedFiles.map((archivo) => {
            return {
              ...archivo,
              codigoCobertura:
                archivo.codigoCobertura ||
                primeraCobertura?.cobertura?.cobertura ||
                "",
            };
          });

          setDocumentosAnexos((prev) => {
            const existingIds = new Set(prev.map((a) => a.id));
            const anexosSinDuplicados = nuevosAnexos.filter(
              (a) => !existingIds.has(a.id)
            );
            return [...prev, ...anexosSinDuplicados];
          });

          setAnexosCompletos((prev) => {
            const existingIds = new Set(prev.map((a) => a.id));
            const anexosSinDuplicados = nuevosAnexos.filter(
              (a) => !existingIds.has(a.id)
            );
            return [...prev, ...anexosSinDuplicados];
          });
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
        return true;
      } catch (error) {
        return false;
      } finally {
        setHojaQuirurgicaLoading(false);
      }
    },
    []
  );

  const handleEnviarCoberturas = useCallback(async (correoValidacion, comentarioValidacionParam, pertinenciaMedica = false) => {
    if (coberturasCompletas.length === 0) {
      return { requiereCoberturas: true };
    }

    const pacienteData =
      consultarPacienteData?.data?.ingresos?.find(
        (p) => p.numeroIngreso === finalNumeroIngreso
      ) ||
      consultarPacientesData?.data?.ingresos?.find(
        (p) => p.numeroIngreso === finalNumeroIngreso
      );

    const comentarioFinal = comentarioValidacionParam || "Validación creada automáticamente";
    const correoFinal = correoValidacion || authUserData?.prestador?.correoElectronico || "";

    const validacionData = construirValidacionData(
      pacienteData,
      authUserData,
      empresa,
      correoFinal,
      comentarioFinal
    );

    setEnviandoCoberturas(true);

    try {
      const respuestaValidacion =
        await HojasQuirurgicasService.validarHojaQuirurgica({
          requestData: validacionData,
          requestHeaders: {},
        });

      const secuencial = respuestaValidacion?.data?.secuencial;

      if (!secuencial) {
        throw new Error("No se obtuvo el secuencial de la validación");
      }

      const codigoUsuario = authUserData?.prestador?.codigo;
      const numeroIngresoNumerico = Number(pacienteData?.secuencial) || Number(pacienteData?.numeroIngreso) || null;
      const nombreUsuario = authUserData?.prestador?.nombre || "";

      let debeMostrarModalEncuesta = false;
      const fechaHoraInicio = ENCUESTA_CES_HORARIO.FECHA_HORA_INICIO;
      const fechaHoraFin = ENCUESTA_CES_HORARIO.FECHA_HORA_FIN;

      if (codigoUsuario) {
        const storageKey = `${LOCAL_STORAGE.FECHA_ULTIMA_ENCUESTA.KEY}_${codigoUsuario}`;
        const existingData = localStorage.getItem(storageKey);
        
        if (existingData && existingData.trim() !== "") {
          try {
            const fechaUltimaEncuestaData = JSON.parse(existingData);
            const fechaHora = fechaUltimaEncuestaData?.fechaHora;
            
            if (fechaHora) {
              if (esFechaDelDiaActual(fechaHora)) {
                debeMostrarModalEncuesta = false;
              } else {
                if (estaHoraEntreRango(fechaHoraInicio, fechaHoraFin)) {
                  debeMostrarModalEncuesta = true;
                } else {
                  debeMostrarModalEncuesta = false;
                }
              }
            } else {
              if (estaHoraEntreRango(fechaHoraInicio, fechaHoraFin)) {
                debeMostrarModalEncuesta = true;
              } else {
                debeMostrarModalEncuesta = false;
              }
            }
          } catch (error) {
            console.error("Error al parsear fechaUltimaEncuesta:", error);
            if (estaHoraEntreRango(fechaHoraInicio, fechaHoraFin)) {
              debeMostrarModalEncuesta = true;
            } else {
              debeMostrarModalEncuesta = false;
            }
          }
        } else {
          if (estaHoraEntreRango(fechaHoraInicio, fechaHoraFin)) {
            debeMostrarModalEncuesta = true;
          } else {
            debeMostrarModalEncuesta = false;
          }
        }
      }

      if (pertinenciaMedica && pacienteData) {
        let numeroIngresoNumerico = Number(pacienteData?.secuencial) || null;

        // if (!numeroIngresoNumerico && pacienteData?.numeroIngreso) {
        //   const parts = String(pacienteData.numeroIngreso).split("-");
        //   numeroIngresoNumerico = parts.length >= 2 ? Number(parts[1]) : Number(pacienteData.numeroIngreso);
        // }
        
        if (!numeroIngresoNumerico) {
          throw new Error("No se obtuvo el número de ingreso");
        }
        const dependienteAsegurado =
          pacienteData?.tipoPersonaHospitalizada === "ASEGURADO" ? 0 : pacienteData.secuenciaDependiente;
        try {
          await HojasQuirurgicasService.actualizarPertinenciaMedica({
            requestData: {
              secuencial,
              numeroIngreso: numeroIngresoNumerico,
              codigoAsegurado: Number(pacienteData?.codigoPersonaHospitalizada) || 0,
              dependienteAsegurado,
              compania: Number(empresa?.CODIGO) || 0,
              pertinenciaMedica: "S",
              usuarioModifica: authUserData?.prestador?.nombre || "",
            },
            requestHeaders: {},
          });
        } catch (errPertinencia) {
          console.error("Error al actualizar pertinencia médica:", errPertinencia);
        }
      }

      const coberturasParaEnviar = coberturasCompletas.filter(
        (c) => !coberturasEnviadas.has(c.id)
      );
      
      // Validar si todas las coberturas ya fueron enviadas anteriormente
      const todasCoberturasYaEnviadas = coberturasParaEnviar.length === 0 && coberturasCompletas.length > 0;
      
      if (todasCoberturasYaEnviadas) {
        setEnviandoCoberturas(false);
        return {
          todasCoberturasYaEnviadas: true,
          tieneDuplicadas: false,
          resultadoEnvio: null,
          debeMostrarModalEncuesta: false,
        };
      }
      
      const tipoReclamante =
        consultarTipoPssNoMedicoData?.tipoReclamante || MODALIDADES_PSS.ARS;

      const {
        todasLasCoberturasExitosas,
        erroresCoberturas,
        nuevasCoberturasEnviadas,
        coberturasDuplicadas: nuevasCoberturasDuplicadas,
        coberturasDuplicadasIds: nuevasCoberturasDuplicadasIds,
        coberturasLiberadas,
      } = await procesarEnvioCoberturas(
        coberturasParaEnviar,
        secuencial,
        tipoReclamante,
        coberturasEnviadas
      );

      setCoberturasEnviadas(nuevasCoberturasEnviadas);

      if (
        nuevasCoberturasDuplicadasIds &&
        nuevasCoberturasDuplicadasIds.size > 0
      ) {
        setCoberturasDuplicadasIds(nuevasCoberturasDuplicadasIds);
      }

      const codigosCoberturasDuplicadas = new Set(
        coberturasCompletas
          .filter((c) => nuevasCoberturasDuplicadasIds?.has(c.id))
          .map((c) => c?.cobertura?.cobertura)
          .filter(Boolean)
      );

      const archivos = anexosCompletos.filter(
        (a) =>
          !archivosEnviados.has(a.id) &&
          !archivosEnviados.has(a.name) &&
          !codigosCoberturasDuplicadas.has(a.codigoCobertura)
      );

      const { todosLosAnexosExitosos, erroresAnexos, nuevosArchivosEnviados } =
        await procesarEnvioAnexos(archivos, secuencial, archivosEnviados);

      setArchivosEnviados(nuevosArchivosEnviados);
      setEnviandoCoberturas(false);

      const todoExitoso =
        todasLasCoberturasExitosas && todosLosAnexosExitosos;

      const resultadoEnvio = construirMensajeResultado(
        todoExitoso,
        coberturasParaEnviar,
        archivos,
        erroresCoberturas,
        erroresAnexos
      );

      if (
        nuevasCoberturasDuplicadas &&
        nuevasCoberturasDuplicadas.length > 0
      ) {
        setCoberturasDuplicadas(nuevasCoberturasDuplicadas);
        setResultadoEnvioPendiente(resultadoEnvio);
        return {
          tieneDuplicadas: true,
          resultadoEnvio,
          coberturasDuplicadas: nuevasCoberturasDuplicadas,
          coberturasLiberadas,
          debeMostrarModalEncuesta,
          secuencial,
          numeroIngreso: numeroIngresoNumerico,
          nombreUsuario,
        };
      } else {
        setCoberturasDuplicadasIds(new Set());
        
        if (todoExitoso && secuencial && numeroIngresoNumerico) {
          const nombreUsuarioEnvio = nombreUsuario || authUserData?.prestador?.nombre || "";
          try {
            await HojasQuirurgicasService.actualizarEnvioHojaQx({
              requestData: {
                secuencial,
                numeroIngreso: numeroIngresoNumerico,
                enviado: "S",
                usuario: nombreUsuarioEnvio,
              },
              requestHeaders: {},
            });
          } catch (error) {
            console.error("Error al actualizar estatus de envío de hoja quirúrgica:", error);
          }
        }
        
        return { 
          tieneDuplicadas: false, 
          resultadoEnvio,
          coberturasLiberadas,
          debeMostrarModalEncuesta,
          secuencial,
          numeroIngreso: numeroIngresoNumerico,
          nombreUsuario,
        };
      }
    } catch (error) {
      console.error("Error al validar hoja quirúrgica:", error);
      setEnviandoCoberturas(false);

      return {
        error: true,
        resultadoEnvio: {
          tipo: "error",
          titulo: "Error al procesar la solicitud",
          mensaje:
            "Ocurrió un error al validar la hoja quirúrgica. Por favor, inténtalo de nuevo.",
          detalles: [
            { cobertura: "N/A", error: error.message || "Error desconocido" },
          ],
        },
      };
    }
  }, [
    coberturasCompletas,
    anexosCompletos,
    consultarPacienteData,
    consultarPacientesData,
    finalNumeroIngreso,
    authUserData,
    empresa,
    consultarTipoPssNoMedicoData?.tipoReclamante,
    coberturasEnviadas,
    archivosEnviados,
  ]);

  const handleEliminarCobertura = useCallback(
    (key) => {
      const coberturaAEliminarObj = coberturasMedicos.find(
        (item) => item.key === key
      );
      const codigoCoberturaAEliminar = coberturaAEliminarObj?.codigoCobertura;
      const idCoberturaAEliminar = coberturaAEliminarObj?.id;

      setCoberturasMedicos((prev) =>
        prev.filter((item) => item.key !== key)
      );

      if (idCoberturaAEliminar) {
        setCoberturasCompletas((prev) =>
          prev.filter((item) => item.id !== idCoberturaAEliminar)
        );

        setCoberturasDuplicadasIds((prev) => {
          const nuevoSet = new Set(prev);
          nuevoSet.delete(idCoberturaAEliminar);
          return nuevoSet;
        });
      }

      if (codigoCoberturaAEliminar) {
        setDocumentosAnexos((prev) =>
          prev.filter(
            (anexo) => anexo.codigoCobertura !== codigoCoberturaAEliminar
          )
        );

        setAnexosCompletos((prev) =>
          prev.filter(
            (anexo) => anexo.codigoCobertura !== codigoCoberturaAEliminar
          )
        );
      }

      if (currentFormData) {
        const coberturaIndex = coberturasMedicos.findIndex(
          (item) => item.key === key
        );
        const coberturasActualizadas =
          currentFormData.coberturasMedicos?.filter(
            (_, index) => index !== coberturaIndex
          ) || [];

        const archivosActualizados = codigoCoberturaAEliminar
          ? currentFormData.attachedFiles?.filter(
              (archivo) => archivo.codigoCobertura !== codigoCoberturaAEliminar
            ) || []
          : currentFormData.attachedFiles || [];

        setCurrentFormData({
          ...currentFormData,
          coberturasMedicos: coberturasActualizadas,
          attachedFiles: archivosActualizados,
        });
      }
    },
    [coberturasMedicos, currentFormData]
  );

  const handleEliminarAnexo = useCallback(
    (archivoId) => {
      setDocumentosAnexos((prev) =>
        prev.filter((archivo) => archivo.id !== archivoId)
      );

      setAnexosCompletos((prev) =>
        prev.filter((archivo) => archivo.id !== archivoId)
      );

      if (currentFormData?.attachedFiles) {
        const archivosActualizados = currentFormData.attachedFiles.filter(
          (archivo) => archivo.id !== archivoId
        );
        setCurrentFormData({
          ...currentFormData,
          attachedFiles: archivosActualizados,
        });
      }
    },
    [currentFormData]
  );

  const todasLasCoberturasEnviadasODuplicadas = useMemo(() => {
    if (coberturasCompletas.length === 0) {
      return true;
    }

    return coberturasCompletas.every(
      (c) => coberturasEnviadas.has(c.id) || coberturasDuplicadasIds.has(c.id)
    );
  }, [coberturasCompletas, coberturasEnviadas, coberturasDuplicadasIds]);

  useMountEffect(() => {
    cargarPacienteConHojaQX();
  });

  // Efecto para recargar el paciente cuando lleguen los datos del backend
  useEffect(() => {
    if (consultarPacienteData || consultarPacientesData) {
      cargarPacienteConHojaQX();
    }
  }, [consultarPacienteData, consultarPacientesData, cargarPacienteConHojaQX]);

  useEffect(() => {
    const tipoReclamante = consultarTipoPssNoMedicoData?.tipoReclamante;
    const debeEjecutarServicio =
      tipoReclamante === MODALIDADES_PSS.PAQUETE ||
      tipoReclamante === MODALIDADES_PSS.HIBRIDO;

    if (obtenerEspecialidadesMedicas && debeEjecutarServicio) {
      obtenerEspecialidadesMedicas({
        offset: 0,
        limit: 100,
      });
    }
  }, [
    consultarTipoPssNoMedicoData?.tipoReclamante,
    obtenerEspecialidadesMedicas,
  ]);

  useEffect(() => {
    if (finalNumeroIngreso && authUserData?.prestador?.codigo) {
      const payload = {
        numeroMedico: authUserData.prestador.codigo,
        params: {
          numeroIngreso: finalNumeroIngreso,
        },
        requestHeaders: {},
      };

      consultarPaciente(payload);
      setCoberturasMedicos([]);
      setDocumentosAnexos([]);
      setCoberturasCompletas([]);
      setAnexosCompletos([]);
      setCoberturasDuplicadasIds(new Set());
      setCurrentFormData(null);
    }
  }, [
    finalNumeroIngreso,
    consultarPaciente,
    authUserData?.prestador?.codigo,
  ]);

  return {
    pacienteIngresado,
    coberturasMedicos,
    documentosAnexos,
    coberturasCompletas,
    anexosCompletos,
    coberturasEnviadas,
    archivosEnviados,
    hojaQuirurgicaLoading,
    enviandoCoberturas,
    currentFormData,
    coberturasDuplicadas,
    coberturasDuplicadasIds,
    resultadoEnvioPendiente,
    todasLasCoberturasEnviadasODuplicadas,
    handleSubmitHojaQuirurgica,
    handleEnviarCoberturas,
    handleEliminarCobertura,
    handleEliminarAnexo,
    cargarPacienteConHojaQX,
    setCoberturasDuplicadas,
    setResultadoEnvioPendiente,
  };
};

export default useValidacionesHojaQX;

